/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Certificate,
  CertificateRequest,
  CertificateTemplate,
  PublicUserCertificateDetails,
  UserCertificateDetails,
} from '@app/app/shared/models/certificate/models/certificate.model';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { ObservableResult } from '@app/app/shared/store';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CertificateDataService } from './certificate-data.service';

@Injectable()
export class ApiCertificateDataService implements CertificateDataService {
  constructor(
    private restClient: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getTemplates(): ObservableResult<CertificateTemplate[]> {
    return this.restClient.get<CertificateTemplate[]>(Location.joinWithSlash(environment.apiRootUrl || '', 'certificates/templates')).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err: HttpErrorResponse) =>
        ObservableResult.ofError(this.resolveErrorMessage(err, 'translations.errors.errorGetCertificateTemplates')),
      ),
    );
  }

  getCertificate(resourceId: string): ObservableResult<Certificate | undefined> {
    return this.restClient
      .get<Certificate>(Location.joinWithSlash(environment.apiRootUrl || '', `resources/${resourceId}/certificate`))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            return ObservableResult.ofSuccess<Certificate>(undefined);
          }
          return ObservableResult.ofError(this.resolveErrorMessage(err, 'translations.errors.errorGetCertificate'));
        }),
      );
  }

  saveCertificate(resourceId: string, request: CertificateRequest): ObservableResult<Certificate> {
    return this.restClient
      .put<Certificate>(Location.joinWithSlash(environment.apiRootUrl || '', `resources/${resourceId}/certificate`), request)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err: HttpErrorResponse) =>
          ObservableResult.ofError(this.resolveErrorMessage(err, 'translations.errors.errorSaveCertificate')),
        ),
      );
  }

  updateCertificateActiveStatus(resourceId: string, status: boolean): ObservableResult<Certificate> {
    return this.restClient
      .patch<Certificate>(Location.joinWithSlash(environment.apiRootUrl || '', `resources/${resourceId}/certificate/status`), {
        active: status,
      })
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            return ObservableResult.ofError(this.translocoService.translate('translations.certificate.message.error.notYetSaved'));
          }
          return ObservableResult.ofError(this.resolveErrorMessage(err, 'translations.errors.errorUpdateCertificateStatus'));
        }),
      );
  }

  downloadSamplePdf(resourceId: string, request: CertificateRequest): Observable<Blob> {
    return this.restClient
      .post<Blob>(
        Location.joinWithSlash(environment.apiRootUrl || '', `resources/${resourceId}/certificate/sample-pdf`),
        request,
        undefined,
        undefined,
        'blob',
      )
      .pipe(map(({ body }) => body));
  }

  getIssuedCertificates(): ObservableResult<UserCertificateDetails[]> {
    return this.restClient.get<UserCertificateDetails[]>(Location.joinWithSlash(environment.apiRootUrl || '', 'certificates/issued')).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body || [])),
      catchError((err: HttpErrorResponse) =>
        ObservableResult.ofError(this.resolveErrorMessage(err, 'translations.errors.errorGetIssuedCertificates')),
      ),
    );
  }

  downloadIssuedPdf(resourceId: string): Observable<Blob> {
    return this.restClient
      .get<Blob>(
        Location.joinWithSlash(environment.apiRootUrl || '', `resources/${resourceId}/certificate/mine/pdf`),
        undefined,
        undefined,
        'blob',
      )
      .pipe(map(({ body }) => body));
  }

  getCertificateVerification(userCertificateUid: string): ObservableResult<PublicUserCertificateDetails | undefined> {
    return this.restClient
      .get<PublicUserCertificateDetails>(Location.joinWithSlash(environment.apiRootUrl || '', `certificates/public/${userCertificateUid}`))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            return ObservableResult.ofSuccess<PublicUserCertificateDetails>(undefined);
          }
          return ObservableResult.ofError(this.resolveErrorMessage(err, 'translations.errors.errorGetCertificate'));
        }),
      );
  }

  /** Prefers the backend's own `{ message }` error body when present, falling back to a generic translated one. */
  private resolveErrorMessage(err: HttpErrorResponse, fallbackKey: string): string {
    return err?.error?.message || this.translocoService.translate(fallbackKey);
  }
}
